/**
 * DV-STAGE-003: Effective Input Computation Hook
 * Computes effective input based on source selection with proper precedence and logging
 */

import { useMemo, useCallback, useRef } from 'react';
import {
  StageId,
  StageTesterState,
  EffectiveInputEnvelope,
  SampleItem
} from '@/types/stage-tester';
import { sessionOutputs } from '@/lib/session-outputs';
import { validateInputSize, INPUT_SIZE_LIMITS } from '@/lib/input-validation';
import { redactForLogging } from '@/lib/redaction';

interface UseEffectiveInputProps {
  stage: StageId;
  state: StageTesterState;
  samples: SampleItem[];
  onLog: (entry: { ts: string; level: 'debug' | 'info' | 'warn' | 'error'; text: string; meta?: any }) => void;
}

interface EffectiveInputResult {
  input: any;
  envelope: EffectiveInputEnvelope | null;
  error: string | null;
  hash: string;
  sizeKB?: number;
  sizeValid?: boolean;
}

// Create stable hash for debugging and logging
function createInputHash(input: any): string {
  try {
    const str = JSON.stringify(input, Object.keys(input).sort());
    // Simple hash function for debugging (not cryptographic)
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(16);
  } catch {
    return 'invalid';
  }
}

export function useEffectiveInput({ stage, state, samples, onLog }: UseEffectiveInputProps) {
  // Use ref to avoid onLog causing recomputation cycles
  const onLogRef = useRef(onLog);
  onLogRef.current = onLog;

  const computeEffectiveInput = useCallback((): EffectiveInputResult => {
    const { source, sampleId, customJson, lastOutputRef } = state;

    // Debug logging removed to prevent render loop spam on computation

    // Input resolution algorithm per DV-STAGE-003
    let input: any = null;
    let error: string | null = null;
    let envelope: EffectiveInputEnvelope | null = null;
    let sizeKB: number = 0;
    let sizeValid: boolean = true;

    try {
      switch (source) {
        case 'custom': {
          if (!customJson?.trim()) {
            error = 'Custom JSON is empty';
            break;
          }

          try {
            input = JSON.parse(customJson);
            envelope = {
              stage,
              source: 'custom',
              input,
              meta: {
                size: customJson.length,
                parsed: true
              }
            };

            onLogRef.current({
              ts: new Date().toISOString(),
              level: 'info',
              text: `Custom JSON parsed successfully for ${stage}`,
              meta: {
                stage,
                source,
                size: customJson.length,
                hash: createInputHash(input)
              }
            });
          } catch (parseError) {
            error = `Invalid JSON: ${parseError instanceof Error ? parseError.message : 'Parse error'}`;
            onLogRef.current({
              ts: new Date().toISOString(),
              level: 'error',
              text: `Custom JSON parse failed for ${stage}`,
              meta: {
                stage,
                source,
                error: error
              }
            });
          }
          break;
        }

        case 'sample': {
          if (!sampleId) {
            error = 'No sample selected';
            break;
          }

          const sample = samples.find(s => s.id === sampleId);
          if (!sample) {
            error = `Sample ${sampleId} not found`;
            onLogRef.current({
              ts: new Date().toISOString(),
              level: 'error',
              text: `Sample not found for ${stage}`,
              meta: {
                stage,
                source,
                sampleId,
                availableSamples: samples.map(s => s.id)
              }
            });
            break;
          }

          input = sample.input;
          envelope = {
            stage,
            source: 'sample',
            input,
            meta: {
              sampleId: sample.id,
              sampleLabel: sample.label,
              tags: sample.tags
            }
          };

          onLogRef.current({
            ts: new Date().toISOString(),
            level: 'info',
            text: `Sample resolved for ${stage}`,
            meta: {
              stage,
              source,
              sampleId: sample.id,
              sampleLabel: sample.label,
              hash: createInputHash(input)
            }
          });
          break;
        }

        case 'last_output': {
          // Try session output first (preferred)
          const sessionOutput = sessionOutputs.getLastOutputForStage(stage);
          if (sessionOutput) {
            input = sessionOutput.output;
            envelope = {
              stage,
              source: 'last_output',
              input,
              meta: {
                origin: 'session',
                sourceStage: sessionOutput.stage,
                completedAt: sessionOutput.completedAt,
                executionId: sessionOutput.executionId
              }
            };

            onLogRef.current({
              ts: new Date().toISOString(),
              level: 'info',
              text: `Session output resolved for ${stage}`,
              meta: {
                stage,
                source,
                origin: 'session',
                sourceStage: sessionOutput.stage,
                hash: createInputHash(input)
              }
            });
            break;
          }

          // Try execution output if session not available
          if (lastOutputRef?.origin === 'execution' && lastOutputRef.executionId) {
            // For now, indicate that execution output would be fetched
            // In a full implementation, this would fetch from the API
            error = 'Execution output fetching not yet implemented';
            onLogRef.current({
              ts: new Date().toISOString(),
              level: 'warn',
              text: `Execution output not available for ${stage}`,
              meta: {
                stage,
                source,
                lastOutputRef
              }
            });
            break;
          }

          error = 'No last output available';
          onLogRef.current({
            ts: new Date().toISOString(),
            level: 'warn',
            text: `No last output available for ${stage}`,
            meta: {
              stage,
              source,
              lastOutputRef
            }
          });
          break;
        }

        default:
          error = `Unknown input source: ${source}`;
          onLogRef.current({
            ts: new Date().toISOString(),
            level: 'error',
            text: `Unknown input source for ${stage}`,
            meta: {
              stage,
              source
            }
          });
      }
    } catch (unexpectedError) {
      error = `Unexpected error: ${unexpectedError instanceof Error ? unexpectedError.message : 'Unknown error'}`;
      onLogRef.current({
        ts: new Date().toISOString(),
        level: 'error',
        text: `Unexpected error computing effective input for ${stage}`,
        meta: {
          stage,
          source,
          error
        }
      });
    }

    // Validate input size if we have valid input
    if (input && !error) {
      const sizeValidation = validateInputSize(input, INPUT_SIZE_LIMITS.effective_input);
      sizeKB = sizeValidation.sizeKB;
      sizeValid = sizeValidation.valid;

      if (!sizeValidation.valid) {
        error = sizeValidation.error || 'Size validation failed';
      }
    }

    const hash = input ? createInputHash(input) : 'none';

    // Emit input_composed log as specified (with redaction)
    if (input && !error) {
      const logMeta = redactForLogging({
        stage,
        source,
        hash,
        sizeKB,
        sizeValid,
        envelope: envelope ? {
          stage: envelope.stage,
          source: envelope.source,
          meta: envelope.meta
        } : undefined
      }, false); // Use feature flag when available

      onLogRef.current({
        ts: new Date().toISOString(),
        level: 'info',
        text: 'input_composed',
        meta: logMeta.redacted
      });
    }

    return {
      input,
      envelope,
      error,
      hash,
      sizeKB,
      sizeValid
    };
  }, [stage, state, samples]); // Removed onLog to prevent feedback loops

  // Memoize the result to avoid unnecessary recomputation
  const result = useMemo(() => {
    return computeEffectiveInput();
  }, [computeEffectiveInput]);

  // Additional memoization by hash for referential stability
  const stableResult = useMemo(() => {
    // Only return new object if hash actually changed
    return {
      input: result.input,
      envelope: result.envelope,
      error: result.error,
      hash: result.hash,
      sizeKB: result.sizeKB,
      sizeValid: result.sizeValid
    };
  }, [result.hash, result.error]); // Only recompute when hash or error changes

  return stableResult;
}

